/**
 * What the client is offered on the meeting card, which is no longer one-to-one
 * with what the order carries.
 *
 * The prototype asks "who will be present on-site?" and answers it with separate
 * options rather than a mode plus a checkbox ticked afterwards. The same
 * question is worth asking of the person the creative CALLS, so both `ON_SITE`
 * and `THIRD_PARTY` are two choices each — the client themselves, or somebody
 * else — and each pair places one mode on the wire. The difference is only whose
 * contact details go in the form, which is exactly what the "I will be onsite"
 * checkbox used to decide.
 *
 * Keeping the split here rather than in `MeetingMode` matters: `MeetingMode` is
 * the wire vocabulary, read by `serializePlacement` and by the backend. Adding a
 * UI distinction to it would put a question about prefill into the order.
 */

import type { MeetingDTO, MeetingMode } from 'models/purchaseFlowRevamp'

/** One card in the meeting picker. */
export type MeetingChoice =
  | 'ON_SITE_SELF'
  | 'ON_SITE_OTHER'
  | 'KEY_PICKUP'
  | 'THIRD_PARTY_SELF'
  | 'THIRD_PARTY_OTHER'
  | 'NONE'

/** The copy key each card reads, under `purchase_flow_revamp:instructions.meeting`. */
export const MEETING_CHOICE_KEY: Record<MeetingChoice, string> = {
  ON_SITE_SELF: 'mode_on_site_self',
  ON_SITE_OTHER: 'mode_on_site_other',
  KEY_PICKUP: 'mode_key_pickup',
  THIRD_PARTY_SELF: 'mode_third_party_self',
  THIRD_PARTY_OTHER: 'mode_third_party_other',
  NONE: 'mode_none',
}

/** Which modes are asked as a self/somebody-else pair, and in which order. */
const SPLIT_MODES: ReadonlyArray<{ mode: MeetingMode, self: MeetingChoice, other: MeetingChoice }> = [
  { mode: 'ON_SITE', self: 'ON_SITE_SELF', other: 'ON_SITE_OTHER' },
  { mode: 'THIRD_PARTY', self: 'THIRD_PARTY_SELF', other: 'THIRD_PARTY_OTHER' },
]

/** The mode a choice places on the wire. Each split pair collapses back to one. */
export const choiceMode = (choice: MeetingChoice): MeetingMode => {
  const split = SPLIT_MODES.find((s) => s.self === choice || s.other === choice)
  return split ? split.mode : (choice as MeetingMode)
}

/** Whether the choice fills the form with the client's own details. */
export const choicePrefills = (choice: MeetingChoice): boolean =>
  SPLIT_MODES.some((s) => s.self === choice)

/**
 * The cards to render, in the order the modes came in.
 *
 * A split mode expands IN PLACE, so the picker keeps whatever ordering
 * `resolveMeetingModes` produced from the catalogue rather than hoisting the
 * pairs to the front.
 */
export const expandMeetingChoices = (modes: readonly MeetingMode[]): MeetingChoice[] =>
  modes.flatMap((mode) => {
    const split = SPLIT_MODES.find((s) => s.mode === mode)
    return split ? [split.self, split.other] : [mode as MeetingChoice]
  })

/**
 * Which card a draft is showing as selected.
 *
 * The two cards of a pair carry the same mode, so the stored pick decides. A draft saved before the pick
 * was stored falls back to the contact details: they read as the client's own when every field the profile
 * also has matches it, and at least one does. A draft with no details yet resolves to the SELF
 * card, which loses nothing if wrong, since picking the other card clears the form anyway.
 */
export const selectedMeetingChoice = (
  meeting: MeetingDTO | undefined,
  prefill: { name?: string, phone?: string, email?: string } | undefined
): MeetingChoice | undefined => {
  if (!meeting) return undefined
  const split = SPLIT_MODES.find((s) => s.mode === meeting.mode)
  if (!split) return meeting.mode as MeetingChoice
  if (meeting.contactIsSelf !== undefined) return meeting.contactIsSelf ? split.self : split.other

  // The two branches spell the same three fields differently.
  const contact = meeting.mode === 'ON_SITE'
    ? { name: meeting.onSite?.contactName, phone: meeting.onSite?.contactPhone, email: meeting.onSite?.contactEmail }
    : { name: meeting.thirdParty?.name, phone: meeting.thirdParty?.phone, email: meeting.thirdParty?.email }

  if (!contact.name && !contact.phone && !contact.email) return split.self
  const pairs = [[contact.name, prefill?.name], [contact.phone, prefill?.phone], [contact.email, prefill?.email]]
  const compared = pairs.filter(([field, own]) => field && own)
  const isSelf = compared.length > 0 && compared.every(([field, own]) => field === own)
  return isSelf ? split.self : split.other
}

/**
 * The meeting to restore when the client switches back to a fixed schedule: the one set aside when they
 * switched to flexible, unless they have since set up a fixed meeting of their own.
 */
export function meetingToRestoreOnFixed(current: MeetingDTO | undefined, parked: MeetingDTO | undefined): MeetingDTO | undefined {
  if (!parked) return undefined
  const isFlexibleMeeting = current?.mode === 'THIRD_PARTY' || !!current?.keyPickup?.arrangedByCreative
  return !current || isFlexibleMeeting ? parked : undefined
}
